import React, { useState } from "react";

interface MessageActionsProps {
  role: "user" | "assistant";
  content: string;
  messageId?: string;
  onCopy?: (content: string) => void;
  onEdit?: () => void;
  onRegenerate?: (messageId: string) => void;
  onFeedback?: (messageId: string, feedback: "positive" | "negative") => void;
  currentFeedback?: "positive" | "negative" | null;
  align: "left" | "right";
}

export default function MessageActions({
  role,
  content,
  messageId,
  onCopy,
  onEdit,
  onRegenerate,
  onFeedback,
  currentFeedback,
  align,
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      onCopy?.(content);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={`flex items-center gap-0.5 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 ${
        align === "right" ? "justify-end" : "justify-start"
      }`}
    >
      {/* Copy */}
      <ActionButton onClick={handleCopy} title={copied ? "Copied!" : "Copy"} active={copied}>
        {copied ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="var(--c-success)"
            strokeWidth="1.5"
          >
            <path d="M3 7.5l2.5 2.5L11 4" />
          </svg>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="4.5" y="4.5" width="7" height="7" rx="1" />
            <path d="M9.5 4.5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v5.5a1 1 0 001 1h1.5" />
          </svg>
        )}
      </ActionButton>

      {/* Edit (user only) */}
      {role === "user" && onEdit && (
        <ActionButton onClick={onEdit} title="Edit">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M8.5 2.5l3 3-7.5 7.5H1v-3l7.5-7.5z" />
          </svg>
        </ActionButton>
      )}

      {/* Regenerate (assistant only) */}
      {role === "assistant" && onRegenerate && messageId && (
        <ActionButton onClick={() => onRegenerate(messageId)} title="Regenerate">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M1.5 7a5.5 5.5 0 019.37-3.9M12.5 7a5.5 5.5 0 01-9.37 3.9" />
            <path d="M10.5 1v2.5H13" />
            <path d="M3.5 13v-2.5H1" />
          </svg>
        </ActionButton>
      )}

      {/* Thumbs up (assistant only) */}
      {role === "assistant" && onFeedback && messageId && (
        <>
          <ActionButton
            onClick={() => onFeedback(messageId, "positive")}
            title="Good response"
            active={currentFeedback === "positive"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill={currentFeedback === "positive" ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 6.5V12H2.5a1 1 0 01-1-1V7.5a1 1 0 011-1H4zm0 0l2-5a1.5 1.5 0 011.5-1.5h0A1.5 1.5 0 019 1.5V4.5h3a1 1 0 011 1v1l-1.5 5.5a1 1 0 01-1 .75H4" />
            </svg>
          </ActionButton>

          {/* Thumbs down */}
          <ActionButton
            onClick={() => onFeedback(messageId, "negative")}
            title="Bad response"
            active={currentFeedback === "negative"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill={currentFeedback === "negative" ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="1.5"
              style={{ transform: "rotate(180deg)" }}
            >
              <path d="M4 6.5V12H2.5a1 1 0 01-1-1V7.5a1 1 0 011-1H4zm0 0l2-5a1.5 1.5 0 011.5-1.5h0A1.5 1.5 0 019 1.5V4.5h3a1 1 0 011 1v1l-1.5 5.5a1 1 0 01-1 .75H4" />
            </svg>
          </ActionButton>
        </>
      )}
    </div>
  );
}

function ActionButton({
  onClick,
  title,
  active,
  children,
}: {
  onClick: () => void;
  title: string;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="p-1 rounded transition-colors"
      style={{
        color: active ? "var(--c-primary)" : "var(--c-text-faint)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "var(--c-hover)";
        if (!active) e.currentTarget.style.color = "var(--c-text-secondary)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "";
        if (!active) e.currentTarget.style.color = "var(--c-text-faint)";
      }}
    >
      {children}
    </button>
  );
}
